import { create } from "zustand";

// Previews of the files picked in this browser session, by source id: the
// local object URL the file tray shows as a thumbnail (and the large
// preview), and a PDF's page count. Nothing here is sent anywhere. Both Ask
// bars of a visit (under the note and in the panel) read the same entries.
// An entry's URL is revoked when its source is removed.
export interface SourcePreview {
  url: string | null;
  kind: "image" | "pdf" | "document";
  pages: number | null;
}

interface SourcePreviewsState {
  byId: Readonly<Record<number, SourcePreview>>;
  set: (id: number, preview: SourcePreview) => void;
  drop: (id: number) => void;
}

const revoke = (url: string | null | undefined): void => {
  if (url && typeof URL.revokeObjectURL === "function")
    URL.revokeObjectURL(url);
};

export const useSourcePreviews = create<SourcePreviewsState>()((set, get) => ({
  byId: {},
  set: (id, preview) => {
    const current = get().byId[id];
    if (current && current.url !== preview.url) revoke(current.url);
    set({ byId: { ...get().byId, [id]: preview } });
  },
  drop: (id) => {
    const current = get().byId[id];
    if (!current) return;
    revoke(current.url);
    const { [id]: _removed, ...rest } = get().byId;
    set({ byId: rest });
  },
}));

// A PDF's page count from its bytes, without a PDF library: the page tree's
// largest /Count. Null when it cannot be read cheaply (compressed object
// streams, very large files).
export const PDF_PAGES_MAX_BYTES = 15 * 1024 * 1024;
export const pdfPageCount = async (file: Blob): Promise<number | null> => {
  if (file.size > PDF_PAGES_MAX_BYTES) return null;
  try {
    const text = new TextDecoder("latin1").decode(await file.arrayBuffer());
    let pages = 0;
    for (const match of text.matchAll(
      /\/Type\s*\/Pages\b[^>]*?\/Count\s+(\d+)|\/Count\s+(\d+)[^>]*?\/Type\s*\/Pages\b/g,
    ))
      pages = Math.max(pages, Number(match[1] ?? match[2] ?? 0));
    if (!pages) pages = [...text.matchAll(/\/Type\s*\/Page(?![s\w])/g)].length;
    return pages > 0 ? pages : null;
  } catch {
    return null;
  }
};
